import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Chatbot = ({ user, T }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        { role: 'assistant', content: `Greetings ${user?.displayName?.split(' ')[0] || 'User'}. I am the U-Jarvis Assistant. How can I help you navigate the Financial Command Center today?` }
      ]);
    }
  }, [isOpen, messages.length, user]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsTyping(true);

    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      if (apiKey && apiKey !== 'your_key_here') {
        const prompt = `You are the U-Jarvis Assistant, an AI built into a premium cyberpunk financial command center app called "U-Jarvis".
The app has these tabs: Dashboard (overview), Salary, Stocks, Expenses, Savings, Liabilities (Cards & EMIs). 
Users can track their net worth, save data to Firebase privately, and use Dark/Light themes.
Be concise, helpful, and speak in a slightly robotic, sophisticated AI tone (like JARVIS from Iron Man).
User message: "${userMsg}"`;
        
        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
        });
        
        if (!response.ok) throw new Error("API call failed");
        
        const data = await response.json();
        const aiText = data.candidates?.[0]?.content?.parts?.[0]?.text || "System glitch. Unable to formulate a response.";
        setMessages(prev => [...prev, { role: 'assistant', content: aiText }]);
      } else {
        // Fallback rule-based logic
        setTimeout(() => {
          let responseText = "I am operating in offline fallback mode because no Gemini API key was detected. Please add REACT_APP_GEMINI_API_KEY to the .env file for full capabilities.";
          const q = userMsg.toLowerCase();
          if (q.includes('expense') || q.includes('add expense')) {
            responseText = "To add an expense, navigate to the 'Expenses' tab. Enter the item name, amount, select whether it was deducted from your Salary or Stocks, and confirm.";
          } else if (q.includes('salary') || q.includes('pay')) {
            responseText = "You can log your monthly salary and deductions in the 'Salary' tab. It automatically updates your Net Worth on the Dashboard.";
          } else if (q.includes('stock') || q.includes('invest')) {
            responseText = "Track your trades in the 'Stocks' tab by entering the order amount and any profit or loss. It contributes to your total stocks income.";
          } else if (q.includes('savings') || q.includes('save')) {
            responseText = "Record your deposits in the 'Savings' tab to track your progress toward the ₹10,00,000 vault target.";
          } else if (q.includes('liability') || q.includes('card') || q.includes('loan') || q.includes('emi')) {
            responseText = "Manage credit cards and loans in the 'Liabilities' tab. Adding an EMI payment will deduct it from your active salary balance.";
          } else if (q.includes('hello') || q.includes('hi')) {
            responseText = "Hello. Systems are online and monitoring your financial data.";
          }
          setMessages(prev => [...prev, { role: 'assistant', content: responseText }]);
        }, 1000);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Error communicating with mainframe. Connection failed." }]);
    } finally {
      setIsTyping(false);
    }
  };

  const F = "'Fira Code', monospace";
  // Fallback colors if T is not fully provided
  const bg = T?.cardGrad || 'rgba(10, 5, 20, 0.95)';
  const border = T?.border || 'rgba(139,0,255,0.3)';
  const text = T?.text || '#fff';
  const primary = T?.sky || '#00ffff';
  const inputBg = T?.inputBg || 'rgba(255,255,255,0.05)';

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed', bottom: 90, right: 24, zIndex: 1000,
              width: 340, height: 480, display: 'flex', flexDirection: 'column',
              background: bg, backdropFilter: 'blur(20px)',
              border: `1px solid ${border}`, borderRadius: 16,
              boxShadow: `0 10px 40px rgba(0,0,0,0.5), 0 0 20px ${primary}33`,
              fontFamily: F, overflow: 'hidden'
            }}
          >
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: primary, boxShadow: `0 0 8px ${primary}`, animation: 'blink 2s infinite' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: text, letterSpacing: '0.1em' }}>U-JARVIS AI</span>
              </div>
              <button onClick={() => setIsOpen(false)} style={{ background: 'transparent', border: 'none', color: text, cursor: 'pointer', fontSize: 16, opacity: 0.6 }}>✕</button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12, scrollbarWidth: 'thin', scrollbarColor: `${primary}55 transparent` }}>
              {messages.map((m, i) => (
                <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                  <div style={{ fontSize: 8, color: T?.slate || '#888', marginBottom: 4, textAlign: m.role === 'user' ? 'right' : 'left', letterSpacing: '0.1em' }}>
                    {m.role === 'user' ? 'YOU' : 'JARVIS'}
                  </div>
                  <div style={{
                    padding: '10px 14px', borderRadius: 12, fontSize: 11, lineHeight: 1.5,
                    background: m.role === 'user' ? `${primary}22` : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${m.role === 'user' ? `${primary}55` : border}`,
                    color: text, borderBottomRightRadius: m.role === 'user' ? 2 : 12, borderBottomLeftRadius: m.role === 'user' ? 12 : 2
                  }}>
                    {m.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div style={{ alignSelf: 'flex-start', padding: '10px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.05)', border: `1px solid ${border}` }}>
                  <span style={{ fontSize: 11, color: primary, animation: 'pulse 1s infinite' }}>Processing...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ padding: 16, borderTop: `1px solid ${border}`, background: 'rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder="Ask U-Jarvis..."
                  style={{
                    flex: 1, background: inputBg, border: `1px solid ${border}`,
                    padding: '10px 14px', borderRadius: 8, color: text,
                    fontSize: 12, fontFamily: F, outline: 'none'
                  }}
                />
                <button
                  onClick={handleSend}
                  style={{
                    background: primary, color: '#000', border: 'none',
                    padding: '0 16px', borderRadius: 8, cursor: 'pointer',
                    fontWeight: 900, fontFamily: F
                  }}
                >&gt;</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          width: 54, height: 54, borderRadius: '50%',
          background: `linear-gradient(135deg, ${primary}, ${T?.violet || '#8B00FF'})`,
          border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 4px 20px ${primary}66`,
          color: '#fff', fontSize: 24, fontWeight: 'bold'
        }}
      >
        <div style={{
          position: 'absolute', inset: -4, borderRadius: '50%',
          border: `1px solid ${primary}`, opacity: 0.5, animation: 'spin 4s linear infinite'
        }} />
        AI
      </motion.button>
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default Chatbot;
